import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, Stack, Typography, Box, IconButton, InputAdornment
} from '@mui/material';
import { Close, Download, Add, Delete } from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import TransactionPDF from './TransactionPDF';

const ManualReceiptModal = ({ open, onClose }) => {
    const [form, setForm] = useState({ 
        name: '', 
        address: '', 
        phone: '', 
        items: [{ description: 'Platform Services', amount: '' }],
        reference: ''
    });

    const handleClear = () => {
        setForm({ name: '', address: '', phone: '', items: [{ description: 'Platform Services', amount: '' }], reference: '' });
        onClose();
    };

    const isFormValid = form.name && form.address && form.items.every(i => i.description && i.amount);

    const totalAmount = form.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    // Generate transaction object
    const transaction = {
        type: 'receipt',
        _id: form.reference || Math.random().toString(36).substr(2, 6).toUpperCase(),
        date: new Date(),
        amount: totalAmount,
        items: form.items,
        description: 'Multiple Items',
        status: 'paid'
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...form.items];
        newItems[index][field] = value;
        setForm({ ...form, items: newItems });
    };

    const handleAddItem = () => {
        setForm({ ...form, items: [...form.items, { description: '', amount: '' }] });
    };

    const handleRemoveItem = (index) => {
        const newItems = form.items.filter((_, i) => i !== index);
        setForm({ ...form, items: newItems });
    };

    const school = {
        name: form.name,
        address: form.address,
        phone: form.phone
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">Manual Receipt Generator</Typography>
                    <IconButton onClick={onClose} size="small"><Close /></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter the details below to generate a receipt for a school not yet registered in the system.
                </Typography>

                <Stack spacing={3}>
                    <TextField
                        label="School Name"
                        fullWidth
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="e.g. Royal Academy"
                    />
                    <TextField
                        label="School Address"
                        fullWidth
                        required
                        value={form.address}
                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                        placeholder="e.g. Plot 42, Kumasi High St, Ghana"
                    />
                    <TextField
                        label="Phone Number (Optional)"
                        fullWidth
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="e.g. +233 54 123 4567"
                    />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>Receipt Items</Typography>
                    {form.items.map((item, index) => (
                        <Stack direction="row" spacing={2} key={index} alignItems="flex-start">
                            <TextField
                                label="Description"
                                fullWidth
                                size="small"
                                required
                                value={item.description}
                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                placeholder="e.g. Term Subscription Fee"
                            />
                            <TextField
                                label="Amount"
                                type="number"
                                sx={{ width: 140 }}
                                size="small"
                                required
                                value={item.amount}
                                onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₵</InputAdornment>,
                                }}
                            />
                            <IconButton 
                                color="error" 
                                onClick={() => handleRemoveItem(index)}
                                disabled={form.items.length === 1}
                                sx={{ mt: 0.5 }}
                            >
                                <Delete />
                            </IconButton>
                        </Stack>
                    ))}
                    
                    <Button 
                        startIcon={<Add />} 
                        onClick={handleAddItem} 
                        size="small" 
                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                    >
                        Add Another Item
                    </Button>
                    <TextField
                        label="Reference Number (Optional)"
                        fullWidth
                        value={form.reference}
                        onChange={(e) => setForm({ ...form, reference: e.target.value })}
                        placeholder="Leave blank to auto-generate"
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button onClick={handleClear} color="inherit">Cancel</Button>

                {isFormValid ? (
                    <PDFDownloadLink
                        document={<TransactionPDF transaction={transaction} school={school} />}
                        fileName={`Receipt_${form.name.replace(/\s+/g, '_')}.pdf`}
                        style={{ textDecoration: 'none' }}
                    >
                        {({ loading }) => (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Download />}
                                disabled={loading}
                            >
                                {loading ? 'Preparing...' : 'Download Receipt'}
                            </Button>
                        )}
                    </PDFDownloadLink>
                ) : (
                    <Button variant="contained" disabled startIcon={<Download />}>
                        Download Receipt
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ManualReceiptModal;
